export const companionPlantingAlgorithm = async (ctx) => {
    const {
        gardenMatrix,
        plants,
        config,
        utils: { validatePlacement, placePlant, createPlantMap, fillRemainingOpenAreas, fetchCompanionPlants }
    } = ctx;

    if (!Array.isArray(gardenMatrix) || !Array.isArray(gardenMatrix[0])) {
        throw new Error('companionPlantingAlgorithm expects gardenMatrix to be a 2D array.');
    }

    const rows = gardenMatrix.length;
    const cols = gardenMatrix[0].length;

    let plantMap = createPlantMap(rows, cols);
    let plantsPlaced = 0;
    const warnings = [];

    // Track remaining counts with unique keys
    const plantCounts = {};
    plants.forEach((plant, index) => {
        const uniqueKey = `${plant.id}_${index}`;
        plantCounts[uniqueKey] = plant.count ?? Infinity;
    });

    // Prepare plants with mapped properties and unique keys
    const normalizedPlants = plants.map((plant, index) => ({
        ...plant,
        name: plant.plant_name || plant.name,
        image: plant.plant_image_url || plant.image,
        veggieImage: plant.plant_image_url || plant.veggieImage,
        plantingWidth: plant.planting_width || plant.plantingWidth || 1,
        plantingHeight: plant.planting_height || plant.plantingHeight || 1,
        uniqueKey: `${plant.id}_${index}`
    }));

    // Fetch companion relationships for all plants
    const companionData = await fetchCompanionPlants(normalizedPlants);

    // Helper function to calculate companion score for a position
    const calculateCompanionScore = (plant, row, col, currentPlantMap) => {
        let score = 0;
        const checkRadius = Math.max(plant.plantingWidth, plant.plantingHeight) + 2; // Check nearby area

        // Check all positions within the radius
        for (let r = Math.max(0, row - checkRadius); r < Math.min(rows, row + checkRadius + plant.plantingHeight); r++) {
            for (let c = Math.max(0, col - checkRadius); c < Math.min(cols, col + checkRadius + plant.plantingWidth); c++) {
                const existingPlant = currentPlantMap[r][c];
                if (!existingPlant) continue;

                // Calculate distance (closer plants have more influence)
                const distance = Math.max(Math.abs(r - row), Math.abs(c - col));
                const distanceMultiplier = Math.max(0, 3 - distance) / 3; // Closer = higher multiplier

                // Find companion relationship
                const plantCompanions = companionData[plant.id] || [];
                const relationship = plantCompanions.find(comp => comp.id === existingPlant.id);

                if (relationship) {
                    switch (relationship.relation) {
                        case 'good':
                            score += (config.goodCompanionMultiplier || 10) * distanceMultiplier;
                            break;
                        case 'moderate':
                            score += (config.moderateCompanionMultiplier || 2) * distanceMultiplier;
                            break;
                        case 'bad':
                            score -= (config.badCompanionMultiplier || 15) * distanceMultiplier;
                            break;
                    }
                }
            }
        }

        return score;
    };

    // Helper function to find the best position for a plant
    const findBestPosition = (plant, currentPlantMap) => {
        const positions = [];

        // Check all valid positions
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                if (validatePlacement(plant, currentPlantMap, gardenMatrix, col, row)) {
                    const score = calculateCompanionScore(plant, row, col, currentPlantMap);
                    positions.push({ row, col, score });
                }
            }
        }

        if (positions.length === 0) return null;

        // Sort by score (highest first) and add some randomness for variety
        const randomness = ctx.randomness ?? 0.1;
        positions.sort((a, b) => {
            const scoreA = a.score + (Math.random() - 0.5) * randomness * 20;
            const scoreB = b.score + (Math.random() - 0.5) * randomness * 20;
            return scoreB - scoreA;
        });

        return positions[0];
    };

    // Phase 1: Strategic placement based on companions
    // Sort plants by those with the most companion relationships first
    const plantsWithCompanionCount = normalizedPlants.map(plant => ({
        ...plant,
        companionCount: (companionData[plant.id] || []).length
    })).sort((a, b) => b.companionCount - a.companionCount);

    // Place plants with strong companion relationships first
    for (const plant of plantsWithCompanionCount) {
        const remaining = plantCounts[plant.uniqueKey];
        if (remaining <= 0) continue;

        let placedCount = 0;
        const maxPlacements = Math.min(remaining, config.strictCounts ? remaining : Math.ceil(remaining * 0.7));

        while (placedCount < maxPlacements) {
            const bestPosition = findBestPosition(plant, plantMap);
            if (!bestPosition) break;

            plantMap = placePlant(plantMap, plant, bestPosition.row, bestPosition.col);
            plantCounts[plant.uniqueKey]--;
            plantsPlaced++;
            placedCount++;

            // Log good placements for user feedback
            if (bestPosition.score > 5) {
                warnings.push(`${plant.name} placed with good companions (score: ${bestPosition.score.toFixed(1)})`);
            } else if (bestPosition.score < -5) {
                warnings.push(`${plant.name} placed despite potential conflicts (score: ${bestPosition.score.toFixed(1)})`);
            }
        }
    }

    // Phase 2: Fill remaining space with unused plants
    const remainingPlants = normalizedPlants.filter(p => plantCounts[p.uniqueKey] > 0);

    if (remainingPlants.length > 0) {
        // Sort remaining plants by size (largest first for better space utilization)
        remainingPlants.sort((a, b) => {
            const areaA = a.plantingWidth * a.plantingHeight;
            const areaB = b.plantingWidth * b.plantingHeight;
            return areaB - areaA;
        });

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                for (const plant of remainingPlants) {
                    const remaining = plantCounts[plant.uniqueKey];
                    if (remaining <= 0) continue;

                    if (validatePlacement(plant, plantMap, gardenMatrix, col, row)) {
                        const score = calculateCompanionScore(plant, row, col, plantMap);

                        // Only place if companion score isn't too negative
                        if (score > -10 || !config.strictCompanions) {
                            plantMap = placePlant(plantMap, plant, row, col);
                            plantCounts[plant.uniqueKey]--;
                            plantsPlaced++;

                            if (score < -5) {
                                warnings.push(`${plant.name} placed with some companion conflicts (score: ${score.toFixed(1)})`);
                            }
                            break;
                        }
                    }
                }
            }
        }
    }

    // Collect unused plants
    const unusedPlants = normalizedPlants
        .filter(p => plantCounts[p.uniqueKey] > 0)
        .map(p => ({
            name: p.name,
            id: p.id,
            remaining: plantCounts[p.uniqueKey],
            plantingWidth: p.plantingWidth,
            plantingHeight: p.plantingHeight,
            reason: 'Could not place due to space constraints or companion conflicts'
        }));

    // Phase 3: Auto-fill if requested
    let filledMap = plantMap;
    if (config.autoFillEmpty) {
        try {
            filledMap = await fillRemainingOpenAreas(gardenMatrix, plantMap, config, plants);
            warnings.push('Filled remaining spaces with compatible filler plants');
        } catch (error) {
            warnings.push('Could not auto-fill remaining spaces');
        }
    }

    // Calculate final stats
    const totalSoilCells = gardenMatrix.flat().filter(cell => cell === 1).length;
    const occupiedCells = filledMap.flat().filter(cell => cell !== null).length;
    const efficiency = totalSoilCells > 0 ? (occupiedCells / totalSoilCells) : 0;

    return {
        plantMap: filledMap,
        plantsPlaced,
        unusedPlants,
        warnings,
        efficiency,
        companionAnalysis: {
            totalCompanionRelationships: Object.keys(companionData).length,
            averageCompanionScore: warnings
                .filter(w => w.includes('score:'))
                .reduce((sum, w) => {
                    const match = w.match(/score: ([\d.-]+)/);
                    return sum + (match ? parseFloat(match[1]) : 0);
                }, 0) / Math.max(1, warnings.filter(w => w.includes('score:')).length)
        }
    };
};

// Helper function to analyze existing garden for companion conflicts
export const analyzeCompanionConflicts = async (plantMap, utils) => {
    const conflicts = [];
    const benefits = [];
    const { fetchCompanionPlants } = utils;

    // Get all unique plants in the garden
    const plantsInGarden = [];
    const plantPositions = new Map();

    for (let r = 0; r < plantMap.length; r++) {
        for (let c = 0; c < plantMap[0].length; c++) {
            const plant = plantMap[r][c];
            if (plant && plant.rootCell) {
                plantsInGarden.push(plant);
                if (!plantPositions.has(plant.id)) {
                    plantPositions.set(plant.id, []);
                }
                plantPositions.get(plant.id).push({ row: r, col: c });
            }
        }
    }

    if (plantsInGarden.length === 0) return { conflicts, benefits };

    // Fetch companion data
    const companionData = await fetchCompanionPlants(plantsInGarden);

    // Check each plant against nearby plants
    for (const [plantId, positions] of plantPositions.entries()) {
        const plant = plantsInGarden.find(p => p.id === plantId);
        const companions = companionData[plantId] || [];

        for (const pos of positions) {
            // Check nearby positions (3x3 area around plant)
            for (let r = pos.row - 3; r <= pos.row + 3; r++) {
                for (let c = pos.col - 3; c <= pos.col + 3; c++) {
                    if (r >= 0 && r < plantMap.length && c >= 0 && c < plantMap[0].length) {
                        const nearbyPlant = plantMap[r][c];
                        if (nearbyPlant && nearbyPlant.id !== plantId) {
                            const relationship = companions.find(comp => comp.id === nearbyPlant.id);
                            if (relationship) {
                                const distance = Math.max(Math.abs(r - pos.row), Math.abs(c - pos.col));

                                if (relationship.relation === 'bad') {
                                    conflicts.push({
                                        plant1: plant.name,
                                        plant2: nearbyPlant.name,
                                        distance,
                                        severity: distance <= 2 ? 'high' : 'moderate'
                                    });
                                } else if (relationship.relation === 'good') {
                                    benefits.push({
                                        plant1: plant.name,
                                        plant2: nearbyPlant.name,
                                        distance,
                                        benefit: distance <= 2 ? 'high' : 'moderate'
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return { conflicts, benefits };
};